import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, Container, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import PaginationComponent from "./PaginationComponent";
import PageHeader from "./PageHeader";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";

function OrderList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(25);
  const [promoCodeFilter, setPromoCodeFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [lastSync, setLastSync] = useState(null);
  const navigate = useNavigate();
  const [sortField, setSortField] = useState("order_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  // useEffect(() => {
  //   const fetchOrders = async () => {
  //     setLoading(true);
  //     setError(null);

  //     try {
  //       const response = await fetch(`${apiUrl}/api/orders`, {
  //         credentials: "include",
  //       });

  //       if (response.ok) {
  //         const data = await response.json();
  //         setOrders(data);
  //       } else if (response.status === 401) {
  //         navigate("/login");
  //         toastr.error("Необходима авторизация.");
  //       } else if (response.status === 403) {
  //         setError("У вас нет доступа к этому разделу.");
  //         toastr.error("У вас нет доступа к этому разделу.");
  //       } else {
  //         setError("Ошибка при получении данных.");
  //         toastr.error("Ошибка при получении данных.");
  //       }
  //     } catch (error) {
  //       setError("Ошибка соединения с сервером.");
  //       toastr.error("Ошибка соединения с сервером.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchOrders();
  // }, [navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${apiUrl}/api/orders`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_SERVICE_API_KEY}`, // Добавляем API_KEY
          },
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          setOrders(data);
          setLastSync(new Date());
        } else if (response.status === 401) {
          navigate("/login");
          toastr.error("Необходима авторизация.");
        } else if (response.status === 403) {
          setError("У вас нет доступа к этому разделу.");
          toastr.error("У вас нет доступа к этому разделу.");
        } else {
          setError("Ошибка при получении данных.");
          toastr.error("Ошибка при получении данных.");
        }
      } catch (error) {
        setError("Ошибка соединения с сервером.");
        toastr.error("Ошибка соединения с сервером.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [apiUrl, navigate]);

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);

    const sortedOrders = [...orders].sort((a, b) => {
      if (a[field] < b[field]) return order === "asc" ? -1 : 1;
      if (a[field] > b[field]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setOrders(sortedOrders);
  };

  const metrics = useMemo(() => {
    const base = Array.isArray(orders) ? orders : [];
    const paidOrders = base.filter((order) => order.is_paid);
    const totalAmount = base.reduce((acc, order) => acc + Number(order.order_amount || 0), 0);
    const paidAmount = paidOrders.reduce((acc, order) => acc + Number(order.order_amount || 0), 0);
    const unpaidAmount = Math.max(totalAmount - paidAmount, 0);
    return {
      totalOrders: base.length,
      paidOrders: paidOrders.length,
      unpaidOrders: base.length - paidOrders.length,
      totalAmount,
      paidAmount,
      unpaidAmount,
      payoutProgress: totalAmount ? Math.round((paidAmount / totalAmount) * 100) : 0,
    };
  }, [orders]);

  if (loading) {
    return (
      <div className="page-loader text-center">
        <Spinner animation="border" variant="secondary" />
        <p className="mt-3 text-muted">Обновляем таблицу…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="page-loader text-center">
        <Alert variant="danger" className="shadow-sm">{error}</Alert>
        <Button className="mt-3 btn-rounded" onClick={() => navigate("/dashboard")}>
          Вернуться в обзор
        </Button>
      </Container>
    );
  }

  const filteredOrders = orders.filter(
    (order) =>
      order.promo_code?.toLowerCase().includes(promoCodeFilter.toLowerCase()) &&
      (paymentStatusFilter === "" ||
        (paymentStatusFilter === "paid" && order.is_paid) ||
        (paymentStatusFilter === "unpaid" && !order.is_paid))
  );

  const currentOrders = filteredOrders.slice(
    (currentPage - 1) * ordersPerPage,
    currentPage * ordersPerPage
  );
  const selectedCount = selectedOrderIds.length;
  const allCurrentSelected =
    currentOrders.length > 0 && currentOrders.every((order) => selectedOrderIds.includes(order.id));

  const fmtNumber = (value) => new Intl.NumberFormat("ru-RU").format(value || 0);
  const fmtCurrency = (value) =>
    new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  const lastSyncLabel = lastSync
    ? new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(lastSync)
    : "—";
  const statusClass = (isPaid) => (isPaid ? "status-pill status-pill--paid" : "status-pill status-pill--pending");

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectCurrentPage = () => {
    const currentPageIds = currentOrders.map((order) => order.id);
    setSelectedOrderIds((prev) => {
      if (currentPageIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !currentPageIds.includes(id));
      }
      return [...new Set([...prev, ...currentPageIds])];
    });
  };

  const openDeleteConfirmation = (orderIds) => {
    setPendingDeleteIds(orderIds);
    setShowDeleteModal(true);
  };

  const handleDeleteOrders = async () => {
    if (!pendingDeleteIds.length) return;

    setIsDeleting(true);
    try {
      const isSingleDelete = pendingDeleteIds.length === 1;
      const endpoint = isSingleDelete
        ? `${apiUrl}/admin/partner/order/${pendingDeleteIds[0]}/delete`
        : `${apiUrl}/admin/orders/delete`;
      const response = await fetch(endpoint, {
        method: isSingleDelete ? "DELETE" : "POST",
        credentials: "include",
        headers: isSingleDelete ? undefined : { "Content-Type": "application/json" },
        body: isSingleDelete ? undefined : JSON.stringify({ order_ids: pendingDeleteIds }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить заказ");
      }

      setOrders((prev) => prev.filter((order) => !pendingDeleteIds.includes(order.id)));
      setSelectedOrderIds((prev) => prev.filter((id) => !pendingDeleteIds.includes(id)));
      setShowDeleteModal(false);
      setPendingDeleteIds([]);
      toastr.success(
        isSingleDelete
          ? "Сделка удалена"
          : `Сделки удалены: ${pendingDeleteIds.length}`
      );
    } catch (deleteError) {
      toastr.error(deleteError.message || "Не удалось удалить сделки");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Container fluid="xl" className="compact py-3">
      <PageHeader
        title="Все заказы"
        subtitle="Сводка по выплатам"
        right={<span className="chip chip-ghost">Обновлено {lastSyncLabel}</span>}
      />

      <section className="section mb-3">
        <div className="kpi-board">
          <div className="metric-card metric-blue">
            <span className="metric-caption">Всего заказов</span>
            <span className="metric-value">{fmtNumber(metrics.totalOrders)}</span>
            <span className="metric-subtext text-muted">Оплачено {fmtNumber(metrics.paidOrders)}</span>
          </div>
          <div className="metric-card metric-orange">
            <span className="metric-caption">Общая сумма</span>
            <span className="metric-value">{fmtCurrency(metrics.totalAmount)} EUR</span>
            <span className="metric-subtext text-muted">{fmtNumber(metrics.totalOrders)} записей</span>
          </div>
          <div className="metric-card metric-green">
            <span className="metric-caption">Выплачено</span>
            <span className="metric-value">{fmtCurrency(metrics.paidAmount)} EUR</span>
            <span className="metric-subtext text-muted">{metrics.payoutProgress}% от объёма</span>
          </div>
          <div className="metric-card metric-gray">
            <span className="metric-caption">В ожидании</span>
            <span className="metric-value">{fmtCurrency(metrics.unpaidAmount)} EUR</span>
            <span className="metric-subtext text-muted">{fmtNumber(metrics.unpaidOrders)} заказа</span>
          </div>
        </div>
      </section>

      <section className="section mb-3">
        <div className="section-heading">
          <div>
            <p className="eyebrow text-muted mb-1">Фильтры</p>
            <h2 className="h5 mb-0">Уточните выдачу</h2>
          </div>
          <span className="chip chip-ghost">Показано {fmtNumber(filteredOrders.length)}</span>
        </div>
        <div className="form-row mt-3 flex-wrap">
          <Form.Control
            type="text"
            placeholder="Промокод или часть названия"
            value={promoCodeFilter}
            onChange={(e) => setPromoCodeFilter(e.target.value)}
          />

          <Form.Select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value="">Все статусы</option>
            <option value="paid">Выплачен</option>
            <option value="unpaid">Не выплачен</option>
          </Form.Select>

          <Button
            variant="outline-secondary"
            className="btn-ghost"
            onClick={() => {
              setPromoCodeFilter("");
              setPaymentStatusFilter("");
            }}
          >
            Сбросить
          </Button>
        </div>
      </section>

      <section className="section">
        <div className="section-heading mb-3">
          <div>
            <p className="eyebrow text-muted mb-1">Действия</p>
            <h2 className="h5 mb-0">Операции с выбранными сделками</h2>
          </div>
          <Button
            variant="outline-danger"
            className="btn-ghost"
            disabled={!selectedCount}
            onClick={() => openDeleteConfirmation(selectedOrderIds)}
          >
            Удалить выбранные ({selectedCount})
          </Button>
        </div>
        <div className="table-wrap">
          <Table responsive className="table-enterprise mb-0">
            <thead>
              <tr>
                <th style={{ width: 48 }}>
                  <Form.Check
                    type="checkbox"
                    checked={allCurrentSelected}
                    onChange={toggleSelectCurrentPage}
                    aria-label="Выбрать сделки на текущей странице"
                  />
                </th>
                <th
                  className={`th-sortable ${sortField === "id" ? "th-active" : ""}`}
                  onClick={() => handleSort("id")}
                >
                  ID {sortField === "id" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "order_date" ? "th-active" : ""}`}
                  onClick={() => handleSort("order_date")}
                >
                  Дата создания сделки {sortField === "order_date" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "partner_id" ? "th-active" : ""}`}
                  onClick={() => handleSort("partner_id")}
                >
                  Партнёр ID {sortField === "partner_id" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "order_amount" ? "th-active" : ""}`}
                  onClick={() => handleSort("order_amount")}
                >
                  Сумма заказа {sortField === "order_amount" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th>Валюта</th>
                <th>Промокод</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedOrderIds.includes(order.id)}
                      onChange={() => toggleOrderSelection(order.id)}
                      aria-label={`Выбрать сделку ${order.id}`}
                    />
                  </td>
                  <td>{order.id}</td>
                  <td>{order.order_date}</td>
                  <td>{order.partner_id}</td>
                  <td className="td-num">{fmtCurrency(order.order_amount)}</td>
                  <td>{order.currency}</td>
                  <td>{order.promo_code || "—"}</td>
                  <td>
                    <span className={statusClass(order.is_paid)}>
                      {order.is_paid ? "Выплачен" : "Не выплачен"}
                    </span>
                  </td>
                  <td>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => openDeleteConfirmation([order.id])}
                    >
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <PaginationComponent
          currentPage={currentPage}
          totalItems={filteredOrders.length}
          itemsPerPage={ordersPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setOrdersPerPage}
        />
      </section>

      <ConfirmDeleteModal
        show={showDeleteModal}
        handleClose={() => {
          if (isDeleting) return;
          setShowDeleteModal(false);
          setPendingDeleteIds([]);
        }}
        handleConfirm={handleDeleteOrders}
        title={pendingDeleteIds.length > 1 ? "Подтвердите массовое удаление" : "Подтвердите удаление"}
        body={
          pendingDeleteIds.length > 1 ? (
            <>Вы уверены, что хотите удалить <strong>{pendingDeleteIds.length}</strong> выбранных сделок из базы данных?</>
          ) : (
            <>Вы уверены, что хотите удалить сделку <strong>#{pendingDeleteIds[0]}</strong> из базы данных?</>
          )
        }
        confirmLabel={isDeleting ? "Удаляем..." : "Удалить"}
      />
    </Container>
  );
}

export default OrderList;
